import React, { useEffect, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { formatPhone } from '../phone';

type Attendee = {
  name: string;
  kids: number;
  bringing: string;
  phone?: string;
  needsTent?: boolean;
  needsRide?: boolean;
  dietary?: string;
  offersTent?: boolean;
  offersRide?: boolean;
};

/**
 * Who's coming, for signed-in users.
 *
 * The browser can't list Cognito users, so this reads the roster Lambda (see
 * lambda/roster) and sends the current access token — the Lambda validates it
 * and returns names only. Phone numbers appear solely for people who opted in
 * via custom:show.phone.number; emails are never returned.
 */
export default function Attendees(): JSX.Element {
  const endpoint = process.env.REACT_APP_ROSTER_URL;
  const [attendees, setAttendees] = useState<Attendee[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!endpoint) {
      setError('The attendee list is not set up yet.');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();
        if (!token) throw new Error('no token');

        const res = await fetch(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(String(res.status));

        const body = await res.json();
        if (cancelled) return;
        setAttendees(body.attendees ?? []);
        setTotal(body.total ?? 0);
      } catch {
        if (!cancelled) setError("Couldn't load the attendee list. Try refreshing.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [endpoint]);

  if (error) {
    return (
      <div className="card">
        <h2>🏕️ Who's Coming</h2>
        <p className="error">{error}</p>
      </div>
    );
  }

  if (attendees === null) {
    return (
      <div className="card">
        <h2>🏕️ Who's Coming</h2>
        <p>Loading…</p>
      </div>
    );
  }

  if (attendees.length === 0) {
    return (
      <div className="card">
        <h2>🏕️ Who's Coming</h2>
        <p>Nobody has been approved yet. Check back soon.</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h2>🏕️ Who's Coming</h2>
      <p>
        <strong>{total}</strong> {total === 1 ? 'person' : 'people'} so far.
      </p>

      <ul className="attendee-list">
        {attendees.map(a => (
          <li key={a.name}>
            <strong>{a.name}</strong>
            {a.kids > 0 && (
              <span className="attendee-meta">
                {' '}
                +{a.kids} {a.kids === 1 ? 'child' : 'children'}
              </span>
            )}
            {(a.needsTent || a.needsRide || a.offersTent || a.offersRide) && (
              <div className="needs">
                {a.needsRide && <span className="need-badge">🚗 Needs a ride</span>}
                {a.needsTent && <span className="need-badge">⛺ Needs a tent</span>}
                {a.offersRide && <span className="offer-badge">🚗 Can give a ride</span>}
                {a.offersTent && <span className="offer-badge">⛺ Can share a tent</span>}
              </div>
            )}
            {a.dietary && (
              <div className="allergy-banner">
                ⚠️ Food allergy / dietary: {a.dietary}
              </div>
            )}
            {a.phone && (
              <div className="attendee-meta">
                <a href={`tel:${a.phone}`}>{formatPhone(a.phone)}</a>
              </div>
            )}
            {a.bringing && a.bringing !== '-' && (
              <div className="attendee-meta">Bringing: {a.bringing}</div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
